/**
 * Press Box → Publish. 2026-10-19.
 *
 * The public stats page for the family, in one place: the unique link
 * (copy, open, replace, remove), how players are shown, and WHAT is public —
 * either automatically as soon as Press Box AI finishes a game, or only
 * after the coach reviews and approves it here. Only published stats ever
 * reach the page; everything else stays private.
 */
import { useCallback, useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Globe, Copy, RefreshCw, Trash2, ExternalLink, CheckCircle2, EyeOff, Loader2, Send, Eye, Mail } from 'lucide-react';
import { IN_PROGRESS } from './PressBoxGamesPage.jsx';
import toast from 'react-hot-toast';
import api from '../../services/api.js';
import { useAuth } from '../../context/AuthContext.jsx';
import PressBoxShell from './PressBoxShell.jsx';
import { PRIVACY_LABELS, PRESS_BOX } from '../../constants/pressBox.js';
import { fmtGameTime } from '../../utils/pressBoxFormat.js';

const fmtDate = (d) => {
  if (!d) return '';
  const [y, m, day] = d.split('-').map(Number);
  return new Date(y, m - 1, day).toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric' });
};

export default function PressBoxPublishPage() {
  const { teamId } = useParams();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data: d } = await api.get(`/teams/${teamId}/press-box`, { params: { season: 'all' } });
      setData(d);
    } catch (e) {
      setData({ error: e.response?.data?.error || 'Could not load' });
    }
  }, [teamId]);
  useEffect(() => { load(); }, [load]);

  const patch = async (body, ok) => {
    setSaving(true);
    try { await api.patch(`/teams/${teamId}/press-box/settings`, body); if (ok) toast.success(ok); await load(); }
    catch (e) { toast.error(e.response?.data?.error || 'Could not save'); }
    finally { setSaving(false); }
  };
  const newLink = async () => {
    if (!window.confirm('Make a new link? The old one stops working for everyone who has it.')) return;
    setSaving(true);
    try { await api.post(`/teams/${teamId}/press-box/settings/public-link`); toast.success('New link made'); await load(); }
    catch { toast.error('Could not make a new link'); } finally { setSaving(false); }
  };
  const removeLink = async () => {
    if (!window.confirm('Remove the link? The page goes dark for everyone, and turning it back on later makes a brand-new address.')) return;
    await patch({ remove_link: true }, 'Link removed — the page is off');
  };
  const copy = async (text) => { try { await navigator.clipboard.writeText(text); toast.success('Link copied'); } catch { toast.error('Copy it from the box'); } };

  const setPublished = async (gameId, published) => {
    try {
      await api.post(`/teams/${teamId}/press-box/games/${gameId}/publish`, { published });
      toast.success(published ? "Published — it moved down to 'On the public page'" : 'Taken off the public page');
      load();
    } catch (e) { toast.error(e.response?.data?.error || 'Could not change it'); }
  };

  if (!data) return <PressBoxShell teamId={teamId} userId={user?.id}><div className="text-secondary text-sm p-4">Loading…</div></PressBoxShell>;
  if (data.error) return <PressBoxShell teamId={teamId} userId={user?.id}><div className="card text-center p-4">{data.error}</div></PressBoxShell>;

  const s = data.settings || {};
  const canEdit = ['admin', 'edit'].includes(data.pbRole) || ['coach', 'manager'].includes(data.role);
  const admin = data.pbRole === 'admin' || data.role === 'coach';
  const played = (data.games || []).filter(g => g.score || g.events > 0 || g.official);
  const waiting = played.filter(g => !g.publishedAt);
  const published = played.filter(g => g.publishedAt);
  const box = { background: 'var(--surface-2)', border: '1px solid var(--border)', borderRadius: 10, padding: 12 };

  const working = (g) => !!g.live || IN_PROGRESS.includes(g.recording?.engine);
  const row = (g, action) => (
    <div key={g.id} className="flex items-center gap-3 px-3 py-2" style={{ borderBottom: '1px solid var(--border)', flexWrap: 'wrap' }}>
      <span className="text-sm" style={{ minWidth: 96, color: 'var(--text-2)' }}>{fmtDate(g.date)} <span style={{ color: 'var(--text-3)' }}>{fmtGameTime(g.time, data.timeFormat)}</span></span>
      <Link to={`/press-box/${teamId}/game/${g.id}`} className="flex-1 text-sm font-semibold" style={{ color: 'var(--text)', textDecoration: 'none', minWidth: 140 }}>
        vs {g.opponent || 'TBA'}
      </Link>
      <span className="display-num" style={{ fontSize: 18, minWidth: 54, textAlign: 'right' }}>{g.score ? `${g.score.us}–${g.score.them}` : '–'}</span>
      {/* 2026-10-19 (per Gilles) — a game still being worked on says so in
          blue, same as the game page's Working line. */}
      {working(g) && (
        <span className="badge flex items-center gap-1" style={{ background: 'var(--ice-a8)', color: 'var(--ice)', border: '1px solid var(--ice-a20, var(--border))' }} data-working-chip>
          <Loader2 size={11} className="animate-spin" /> Working…
        </span>
      )}
      {g.needsReview > 0 && <span className="badge" style={{ background: 'var(--puck-a10)', color: 'var(--puck)', border: '1px solid var(--puck-a20)' }}>{g.needsReview} to check</span>}
      {action}
    </div>
  );

  return (
    <PressBoxShell teamId={teamId} userId={user?.id}>
      {/* the link */}
      <div className="card mb-4" data-publish-link>
        <div className="flex items-center justify-between gap-3 mb-2" style={{ flexWrap: 'wrap' }}>
          <div className="flex items-center gap-2 font-bold"><Globe size={16} style={{ color: 'var(--ice)' }} /> The family&rsquo;s stats page</div>
          {admin && (
            <label className="flex items-center gap-2 text-sm" style={{ cursor: 'pointer' }}>
              <input type="checkbox" checked={!!s.publicEnabled} disabled={saving}
                onChange={(e) => patch({ public_enabled: e.target.checked }, e.target.checked ? 'Page is live' : 'Page is off')} />
              {s.publicEnabled ? 'Live' : 'Off'}
            </label>
          )}
        </div>
        <div className="text-sm mb-3" style={{ color: 'var(--text-2)' }}>
          A read-only, phone-friendly page with the published scores, stats, game summaries and play-by-play.
          The address is unguessable — only people you share it with can open it, and search engines are told to stay out.
        </div>
        {s.publicEnabled && s.publicUrl ? (
          <div style={box}>
            <div className="text-xs mb-2" style={{ color: 'var(--text-2)' }}>
              <strong style={{ color: 'var(--text)' }}>This is the link to share</strong> — copy it, email it, or post it in the team chat; everyone with it sees the published games below.
            </div>
            <div className="flex items-center gap-2" style={{ flexWrap: 'wrap' }}>
              <input readOnly className="input-field" style={{ flex: 1, minWidth: 220, padding: '8px 12px', fontFamily: 'var(--font-mono)', fontSize: 12 }} value={s.publicUrl} onFocus={(e) => e.target.select()} />
              <button type="button" className="btn-ice" onClick={() => copy(s.publicUrl)}><Copy size={13} /> Copy</button>
              <a className="btn-ghost" href={`mailto:?subject=${encodeURIComponent('Team stats page')}&body=${encodeURIComponent('Our team stats live here: ' + s.publicUrl)}`} title="Email the link">
                <Mail size={13} /> Email it
              </a>
              <a className="btn-ghost" href={s.publicUrl} target="_blank" rel="noreferrer"><ExternalLink size={13} /> Open</a>
              {admin && <button type="button" className="btn-ghost" onClick={newLink} disabled={saving}><RefreshCw size={13} /> New link</button>}
              {admin && <button type="button" className="btn-danger" onClick={removeLink} disabled={saving}><Trash2 size={13} /> Remove</button>}
            </div>
          </div>
        ) : (
          <div className="text-sm" style={{ color: 'var(--puck)' }}>
            The page is <strong>off</strong> — nobody can see the published games below, and there is no link yet.
            {admin && (
              <button type="button" className="btn-ice" style={{ marginLeft: 10, padding: '6px 12px' }} disabled={saving}
                onClick={() => patch({ public_enabled: true }, 'Page is live — here is your link to share')}>
                <Globe size={13} /> Create the link &amp; go live
              </button>
            )}
          </div>
        )}
        {admin && (
          <label className="flex items-center gap-2 text-sm mt-3" style={{ cursor: 'pointer' }} data-pbp-download-toggle>
            <input type="checkbox" checked={!!data.pbpDownload} disabled={saving}
              onChange={(e) => patch({ pbp_download: e.target.checked }, e.target.checked ? 'Families can download the play-by-play' : 'Play-by-play download is off')} />
            Let families download the play-by-play as a text file
            <span className="text-xs" style={{ color: 'var(--text-3)' }}>(never the transcript — that stays with you)</span>
          </label>
        )}
        {admin && (
          <div className="mt-3">
            <span className="label">Show players as</span>
            {Object.entries(PRIVACY_LABELS).map(([v, l]) => (
              <label key={v} className="flex items-center gap-2 text-sm mb-1" style={{ cursor: 'pointer' }}>
                <input type="radio" name="pub-privacy" checked={s.publicPrivacy === v} disabled={saving}
                  onChange={() => patch({ public_privacy: v }, 'Saved')} /> {l}
              </label>
            ))}
          </div>
        )}
      </div>

      {/* what gets published */}
      {admin && (
        <div className="card mb-4" data-publish-mode>
          <div className="font-bold mb-2">When stats go public</div>
          {[['review', 'Review first', `Each game waits below until you look it over and press Publish.`],
            ['auto', 'Publish automatically', `As soon as ${PRESS_BOX.AI_NAME} finishes a game, its stats go straight to the page.`]].map(([v, l, d]) => (
            <label key={v} className="flex items-start gap-2 text-sm mb-1" style={{ cursor: 'pointer' }}>
              <input type="radio" name="pub-mode" style={{ marginTop: 3 }} checked={(s.publishMode || 'review') === v} disabled={saving}
                onChange={() => patch({ publish_mode: v }, v === 'auto' ? 'New games publish automatically' : 'New games wait for your review')} />
              <span><strong>{l}</strong> <span style={{ color: 'var(--text-3)' }}>— {d}</span></span>
            </label>
          ))}
        </div>
      )}

      {/* review queue */}
      {waiting.length > 0 && (
        <div className="card mb-4" style={{ padding: 0, overflow: 'hidden' }} data-publish-waiting>
          <div className="px-3 py-2 font-bold text-sm" style={{ borderBottom: '1px solid var(--border)' }}>Waiting for review ({waiting.length})</div>
          {waiting.map(g => row(g, (
            <>
              <Link className="btn-ghost" style={{ padding: '6px 10px', textDecoration: 'none' }} to={`/press-box/${teamId}/game/${g.id}/preview`}
                title="See this game exactly as the families would — before publishing">
                <Eye size={13} /> Preview
              </Link>
              {canEdit && (
                <button type="button" className="btn-ice" style={{ padding: '6px 12px' }} onClick={() => setPublished(g.id, true)}>
                  <Send size={13} /> Publish
                </button>
              )}
            </>
          )))}
        </div>
      )}

      {/* published */}
      <div className="card mb-6" style={{ padding: 0, overflow: 'hidden' }} data-publish-done>
        <div className="px-3 py-2 font-bold text-sm flex items-center gap-2" style={{ borderBottom: '1px solid var(--border)' }}>
          <CheckCircle2 size={14} style={{ color: 'var(--goal)' }} /> On the public page ({published.length})
        </div>
        <div className="px-3 py-2 text-xs" style={{ color: 'var(--text-3)', borderBottom: '1px solid var(--border)' }}>
          View opens each game exactly as the families see it. Games land here when YOU press Publish — and
          automatically while &ldquo;Publish automatically&rdquo; is (or was) selected above, which is how games you never
          pressed Publish on can appear here. Unpublish any of them below.
        </div>
        {published.length === 0 && <div className="p-3 text-sm" style={{ color: 'var(--text-3)' }}>Nothing published yet.</div>}
        {published.map(g => row(g, (
          <>
            {g.publishedAt && <span className="text-xs" style={{ color: 'var(--text-3)' }} title="When it went public">{new Date(g.publishedAt).toLocaleDateString([], { month: 'short', day: 'numeric' })}</span>}
            {s.publicEnabled && s.publicUrl && (
              <a className="btn-ghost" style={{ padding: '6px 10px' }} href={`${s.publicUrl}/game/${g.id}`} target="_blank" rel="noreferrer"
                title="Open this game as the families see it" data-view-as-parent>
                <ExternalLink size={13} /> View
              </a>
            )}
            {canEdit && (
              <button type="button" className="btn-ghost" style={{ padding: '6px 10px' }} onClick={() => setPublished(g.id, false)} title="Take this game off the public page">
                <EyeOff size={13} /> {saving ? <Loader2 size={12} className="animate-spin" /> : 'Unpublish'}
              </button>
            )}
          </>
        )))}
      </div>
    </PressBoxShell>
  );
}
